import { useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Row, LoadingSkeleton, Button, Drawer } from "@narmi/design_system";
import { useSetChatVisibility } from "cerulean";
import type { PaymentCancelAction } from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { getReverseRoute } from "../routes";
import { DrawerHeader } from "../common";
import getPaymentTypeDrawerConfig from "./paymentTypeDrawerConfig";
import usePaymentDrawerData from "./usePaymentDrawerData";
import usePaymentActions from "./usePaymentActions";
import TopCard from "./cards/TopCard";
import PaymentDetailsCard from "./cards/PaymentDetailsCard";
import RecipientDetailsCard from "./cards/RecipientDetailsCard";

export type PaymentMetadataProps = {
  entry_desc?: API.ACHPayment["entry_desc"];
  std_ent_cls_code?: API.ACHPayment["std_ent_cls_code"];
  description?: API.ACHPayment["description"];
  memo?:
    | API.ACHPayment["memo"]
    | API.SwiftTransaction["memo"]
    | API.ScheduledSwiftWireTransfer["memo"];
  note?: API.SwiftTransaction["note"] | API.ScheduledSwiftWireTransfer["note"];
  company_name?: API.ACHPayment["company_name"];
  company_id?: API.ACHPayment["company_id"];
  payment_identification?:
    | API.FedNowTransaction["payment_identification"]
    | API.SwiftTransaction["payment_identification"];
  reject_reason_code?: API.FedNowTransaction["reject_reason_code"];
  wire_reason?:
    | API.Wire["wire_reason"]
    | API.ScheduledSwiftWireTransfer["wire_reason"];
  memo_1?: API.Wire["memo_1"];
  memo_2?: API.Wire["memo_2"];
  memo_3?: API.Wire["memo_3"];
  memo_4?: API.Wire["memo_4"];
  beneficiary_address_1?: API.Wire["beneficiary_address_1"];
  beneficiary_address_2?: API.Wire["beneficiary_address_2"];
  beneficiary_address_3?: API.Wire["beneficiary_address_3"];
  template_name?: API.SwiftTransaction["template_name"];
  is_hold_cancelable?: API.SwiftTransaction["is_hold_cancelable"];
  is_cancelable?: API.ScheduledSwiftWireTransfer["is_cancelable"];
  next_transfer_at?: API.ScheduledSwiftWireTransfer["next_transfer_at"];
  cancelable_until?: API.ScheduledSwiftWireTransfer["cancelable_until"];
  specified_currency?: API.ScheduledSwiftWireTransfer["specified_currency"];
  converted_currency?: API.ScheduledSwiftWireTransfer["converted_currency"];
  specified_amount?: API.ScheduledSwiftWireTransfer["specified_amount"];
  quote?: API.FetchExchangeRateQuote.Response;
};

export type PaymentActivityDrawerProps = {
  isOpen: boolean;
  payment: API.FormattedPaymentActivity | null;
  handleClose: () => void;
  handleToggleCancelDialog?: (target: PaymentCancelAction) => void;
  handleOnNext: () => void;
  handleOnPrev: () => void;
  paymentUuid:
    | API.ScheduledACHPaymentId
    | API.ACHPaymentId
    | API.SwiftTransactionId;
  /** Hide all footer actions, e.g. on the reversal selection screen */
  hideFooterActions?: boolean;
};

const PaymentActivityDrawer = ({
  isOpen,
  payment,
  handleClose,
  handleToggleCancelDialog,
  handleOnNext,
  handleOnPrev,
  paymentUuid,
  hideFooterActions = false,
}: PaymentActivityDrawerProps) => {
  const drawerIdRef = useRef(
    `Drawer-${Math.random().toString(36).slice(2, 11)}`
  );
  useSetChatVisibility(drawerIdRef.current, !isOpen);
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    paymentActivity,
    paymentMetadata,
    institutionDetails,
    renameFields,
    correspondentBankName,
    bulkTitle,
    isBulkImported,
    wireImportBatchTitle,
    loading,
  } = usePaymentDrawerData({ payment, paymentUuid });

  const { hasDualApprovalPermission, getActions } = usePaymentActions();

  const drawerConfig = useMemo(
    () =>
      getPaymentTypeDrawerConfig({
        l10n,
        institutionDetails,
        renameFields,
        correspondentBankName,
      }),
    [l10n, institutionDetails, renameFields, correspondentBankName]
  );

  if (!paymentActivity) return null;

  const { uuid } = paymentActivity;
  const { showCancelButton, showReverseButton, cancelAction } = getActions(
    paymentActivity,
    paymentMetadata
  );

  const showCancel = !hideFooterActions && showCancelButton;
  const showReverse = !hideFooterActions && showReverseButton;

  return (
    <Drawer
      depth="524px"
      isOpen={isOpen}
      onUserDismiss={handleClose}
      paddingSize="xl"
      onNext={handleOnNext}
      onPrev={handleOnPrev}
      {...((showCancel || showReverse) && {
        footer: (
          <Row justifyContent="end">
            {showReverse && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() =>
                    navigate(`${getReverseRoute(uuid)}${location.search}`)
                  }
                  label={l10n.getString("label-reverse-payment")}
                />
              </Row.Item>
            )}
            {showCancel && (
              <Row.Item shrink>
                <Button
                  kind="primary"
                  onClick={() =>
                    cancelAction && handleToggleCancelDialog?.(cancelAction)
                  }
                  label={l10n.getString("label-cancel-payment")}
                />
              </Row.Item>
            )}
          </Row>
        ),
      })}
    >
      <LoadingSkeleton content="paragraph" showTitle isLoading={loading}>
        <DrawerHeader
          title={l10n.getString("payment-activity-drawer-title")}
          status={paymentActivity.displayStatus}
        />
        <TopCard
          paymentActivity={paymentActivity}
          paymentMetadata={paymentMetadata}
          bulkTitle={bulkTitle}
          isBulkImported={isBulkImported}
          wireImportBatchTitle={wireImportBatchTitle}
          hasDualApprovalPermission={hasDualApprovalPermission}
        />
        <PaymentDetailsCard
          paymentActivity={paymentActivity}
          paymentMetadata={paymentMetadata}
          drawerConfig={drawerConfig}
        />
        <RecipientDetailsCard
          paymentActivity={paymentActivity}
          paymentMetadata={paymentMetadata}
          drawerConfig={drawerConfig}
        />
      </LoadingSkeleton>
    </Drawer>
  );
};

export default PaymentActivityDrawer;
